import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchAndFormatUserDetails, formFields, hasPermission, selectLoader } from 'utils/Helpers';
import { useAuth } from 'contexts/AuthContext';
import boardToast, { TOAST_TYPE } from './utils/boardToast';
import { createProjectBoard } from './utils/fetchData';
import { ProjectListStrings } from '../../projects/strings/ProjectStrings';
import Header from 'components/Header';
import ProjectBoardHeader from './ProjectBoardHeader';
import BoardList from './BoardList/BoardList';
import ProjectBoardWrapperPage from './Board';
import EditableText from './shared/EditableText';
import ViewTabsBar from './ViewTabsBar/ViewTabsBar';
import UpdatedOverview from 'pages/projects/UpdatedOverview';
import './ProjectBoardPage.css';
import SidebarModal from 'components/SidebarModal';
import UpdateProfileModal from '../homeModals/UpdateProfileModal';
import ProfileModalContent from '../homeModals/ProfileModalContent';
import Notifications from 'components/Notifications';
import SavedTemplates from './SavedTemplates/SavedTemplates';
import APIServices from 'services/APIServices';
import { SocketProvider } from './context/SocketContext';
import { fetchProjectData } from 'utils/fetchData';
import EmptyBoardState from 'components/EmptyBoardState';
import leftArrow from 'assets/img/white-left-icon.svg';

const ProjectBoard = () => {
  const { projectId, childBoardId } = useParams();
  const { user, userRole, subInfo, logout, fetchUserInfo, socket } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  let isProjectRelatedBoard;

  if (location.state && 'isProjectRelatedBoard' in location.state) {
    isProjectRelatedBoard = location.state.isProjectRelatedBoard;
  } else {
    isProjectRelatedBoard =
      location.pathname.includes('/project/') || location.pathname.includes('/project-overview/') || false;
  }
  const showBack = location.state?.showBack || null;
  const isArchivedParent = location.state?.isArchivedParent;

  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [selectedParentBoardId, setSelectedParentBoardId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [projectDetail, setProjectDetail] = useState();
  const [, setError] = useState(null);
  const [isUnread, setUnread] = useState(false);
  const [projects, setProjects] = useState([]);
  const [channels, setChannels] = useState([]);
  const [profileUrl, setProfileUrl] = useState('');
  const [formValues, setFormValues] = useState(formFields);
  const [showSavedTemplates, setShowSavedTemplates] = useState(false);
  const [showArchivedList, setShowArchivedList] = useState(false);
  const [isArchivedActive, setArchivedActive] = useState(false);
  const [isTemplateActive, setTemplateActive] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState('');
  const [archiveBoardId, setArchiveBoardId] = useState('');
  const [tempates, setTemplates] = useState([]);
  const [archivedTempates, setArchivedTempates] = useState([]);
  const [boardTitle, setBoardTitle] = useState('');
  const [workSpaceTitle, setWorkSpaceTitle] = useState('');
  const [, setMainProjectBoardId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [isVerified, setVerified] = useState({
    isEmailVerified: true,
    isPhoneNumberVerified: true
  });
  const [modals, setModals] = useState({
    profileModal: false,
    updateProfile: false,
    updateProject: false,
    notifications: false
  });
  const [showEmptyBoardState, setShowEmptyBoardState] = useState(false);
  const [isCreatingFromTemplate, setIsCreatingFromTemplate] = useState(false);
  const [isBoardListCollapsed, setBoardListCollapsed] = useState(false);
  const isFromHome = location?.state?.isFromHome || false;
  // Tabs at top level - controls full-screen view switching
  const [boardViewType, setBoardViewType] = useState(() => {
    const allowedTabs = new Set(['overview', 'board', 'calendar']);
    const state = location?.state || {};
    const fromState = state.boardViewType || state.activeTab || state.tab;
    if (localStorage?.getItem('boardViewType') !== 'null' && localStorage?.getItem('boardViewType') !== null) return localStorage.getItem('boardViewType');
    if (location.pathname.includes('/project-overview/')) return 'overview';
    if (fromState && allowedTabs.has(fromState)) return fromState;

    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('tab') || params.get('view');
    if (fromQuery && allowedTabs.has(fromQuery)) return fromQuery;

    return 'board';
  });
  const [isOverviewLoaded, setIsOverviewLoaded] = useState(false);

  const toggleBoardList = () => {
    setBoardListCollapsed((prev) => !prev);
  };

  const toggleModal = (modalType, isOpen) => {
    setModals((prevState) => ({
      ...prevState,
      [modalType]: isOpen
    }));
  };
  const loadProject = async () => {
    if (!projectId) return;

    const data = await fetchProjectData(projectId);

    if (data) {
      setProjectName(data.projectName);
    }
  };

  //   useEffect(() => {
  //   const interval = setInterval(() => {
  //     window.location.reload();
  //   }, 20000); // 30 sec = 30000 ms

  //   return () => clearInterval(interval); // cleanup when component unmounts
  // }, []);

  const getUserDetails = async () => {
    const result = await fetchAndFormatUserDetails(logout, fetchUserInfo, user);
    if (result) {
      setFormValues((prev) => ({ ...prev, ...result.formValues }));
      setVerified(result.verified);
    }
  };

  useEffect(() => {
    getUserDetails();

    return () => {
      localStorage.setItem('boardViewType', "null");
    }
  }, []);

  const fetchProjectBoards = useCallback(
    async (showLoader = true, boardId = '') => {
      try {
        setIsLoading(showLoader);
        let projectType = isProjectRelatedBoard ? 'projectId' : 'projectBoardId';
        let response;
        if (projectId)
          response = await APIServices.get(
            `/project-board/child/boards?archived=${isArchivedParent}&${projectType}=${projectId}`
          );
        else response = await APIServices.get(`/project-board?filter=all`);
        if (response && response.projectBoards?.length > 0) {
          const singleBoard = response.projectBoards[0];
          if (!singleBoard.name) {
            setShowEmptyBoardState(true);
          }
          setProjectDetail(response);
          setBoards(response?.projectBoards);
          if (childBoardId?.length > 0 || (boardId?.length > 0 && !isArchivedParent)) {
            let selectedBoard;
            if (childBoardId?.length > 0) {
              selectedBoard = response?.projectBoards.find((x) => x._id === childBoardId);
            } else {
              selectedBoard = response?.projectBoards.find((x) => x._id === boardId);
            }
            setSelectedBoardId(selectedBoard._id);
            setMainProjectBoardId(response?.projectBoards[0]?._id);
            setSelectedParentBoardId(selectedBoard.parentBoardId || response?.projectBoards[0]?._id);
            setChannels(selectedBoard.channels);
            setShowSavedTemplates(false);
            setActiveTemplateId(null);
            setTemplateActive(false);

            setWorkSpaceTitle(response?.projectBoards[0].workspaceName || '');
            setBoardTitle(selectedBoard.name);
          } else {
            setMainProjectBoardId(response?.projectBoards[0]?._id);
            setSelectedBoardId(response?.projectBoards[0]?._id);
            setSelectedParentBoardId(response?.projectBoards[0]?.parentBoardId || response?.projectBoards[0]?._id);
            setChannels(response?.projectBoards[0].channels);
            setWorkSpaceTitle(response?.projectBoards[0].workspaceName || '');
            setBoardTitle(response?.projectBoards[0].workspaceName || response?.projectBoards[0].name);
          }
          if (isArchivedParent) {
            setArchiveBoardId(response?.projectBoards[0]?._id);
            // setShowArchivedList(false);
            // setArchivedActive(false);
            // setShowSavedTemplates(false);
            // setSelectedBoardId(null);
          }
        } else {
          if (!isFromHome) {
            navigate('/project-boards');
          }
        }
      } catch (error) {
        console.error('Error fetching project boards:', error.message || error);
      } finally {
        setIsLoading(false);
      }
    },
    [projectId]
  );

  const fetchProjects = useCallback(async () => {
    try {
      const response = await APIServices.get(`/project-board`);
      if (response.projectBoards) {
        const formatData = response.projectBoards.map((value) => ({
          label: value.name,
          value: value.isProjectRelatedBoard ? value.projectId : value._id,
          isProjectRelatedBoard: value.isProjectRelatedBoard
        }));
        setProjects(formatData);
      } else if (response?.data?.message) {
        boardToast(response?.data?.message, TOAST_TYPE.ERROR);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      boardToast('Error fetching projects:', TOAST_TYPE.ERROR);
    }
  }, []);

  const fetchTemplates = useCallback(
    async (pageNum = 1) => {
      try {
        const response = await APIServices.get(
          `/project-board-templates/company/${user?.companyId?._id}?page=${pageNum}&pageSize=30`
        );
        if (response) {
          const templates = response?.templates || [];
          setTemplates((prev) => (pageNum === 1 ? templates : [...prev, ...templates]));
          return templates.length > 0;
        } else if (response?.data?.message) {
          boardToast(response?.data?.message, TOAST_TYPE.ERROR);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
      return false;
    },
    [user?.companyId?._id]
  );

  const fetchArchivedBoards = useCallback(
    async (pageNum = 1, boardId = selectedParentBoardId) => {
      try {
        const response = await APIServices.get(`/project-board?archived=true&projectBoardId=${boardId}`);
        if (response) {
          const boards = response?.projectBoards || [];
          setArchivedTempates((prev) => (pageNum === 1 ? boards : [...prev, ...boards]));
          return boards.length > 0;
        } else if (response?.data?.message) {
          boardToast(response?.data?.message, TOAST_TYPE.ERROR);
        }
      } catch (error) {
        console.error('Error fetching archived boards:', error);
        boardToast('Error fetching archived boards:', TOAST_TYPE.ERROR);
      }
      return false;
    },
    [user?.companyId?._id]
  );

  useEffect(() => {
    if (!isArchivedParent) {
      loadProject();
    }
    fetchProjectBoards();
  }, []);

  const handleProjectClick = (boardId, isArchived = false) => {
    setSelectedBoardId(boardId);
    setArchiveBoardId(boardId);
    if (!isArchived) {
      setTemplateActive(false);
      setArchivedActive(false);
    }
    setShowArchivedList(false);
    setShowSavedTemplates(false);
  };

  const handleSubmitTitle = async (boardId, value) => {
    socket.emit('update-project-board', {
      id: boardId,
      payload: { name: value }
    });

    setBoards((prevState) =>
      prevState.map((state) => {
        if (state._id === boardId) {
          return { ...state, name: value };
        }
        return state;
      })
    );

    // if (boardId === selectedBoardId) {
    //   setBoardTitle(value);
    // }
  };

  const handleSavedTemplateClick = async (isOpen, isArchived = false) => {
    if (isArchived) {
      setShowArchivedList(isOpen);
      setArchivedActive(isOpen);
      setActiveTemplateId(null);
      setShowSavedTemplates(false);
      setSelectedBoardId(null);
      setTemplateActive(false);
      if (isOpen) {
        setIsLoading(true);
        try {
          await fetchArchivedBoards(1, selectedParentBoardId);
        } finally {
          setIsLoading(false);
        }
      }
    } else {
      setShowSavedTemplates(isOpen);
      setArchivedActive(false);
      setShowArchivedList(false);
      setTemplateActive(false);
    }
  };

  const handleAddBoard = () => {
    if (!boards.some((value) => value._id === 'NEW')) {
      setBoards((prevState) => [...prevState, { name: '', _id: 'NEW' }]);
      setSelectedBoardId('NEW');
    }
  };

  const handleChangeTitle = (value) => {
    setBoards((prev) => prev.map((v) => (v._id === 'NEW' ? { ...v, name: value } : v)));
  };

  const handleCloseTitle = () => {
    const update = boards.filter((v) => v._id !== 'NEW');
    setBoards(update);
    setSelectedBoardId(update[update?.length - 1]?._id);
  };

  const handleSubmit = async (title) => {
    try {
      const response = await createProjectBoard(title, projectId, false, selectedParentBoardId);
      if (response) {
        boardToast('Board created Successfully', TOAST_TYPE.SUCCESS);
        const filterBoard = boards.filter((board) => board._id !== 'NEW');
        setBoards((prevState) => [...filterBoard, response]);
        setSelectedBoardId(response._id);
      }
    } catch (error) {
      console.error('Error creating project board:', error);
      boardToast(error.response?.data?.message || error.message || 'Failed to create project board.', TOAST_TYPE.ERROR);
    }
  };

  const handleTemplateClick = async (templateId) => {
    try {
      setIsLoading(true);
      const response = await APIServices.get(`/project-board-templates/${templateId}`);
      if (response) {
        setActiveTemplateId(templateId);
        setTemplateActive(true);
        setShowSavedTemplates(false);
        setChannels(response?.channels);
        setProjectDetail(response);
      } else {
        setIsLoading(false);
        throw new Error('No template found.');
      }
    } catch (error) {
      console.error('Error fetching template:', error.message || error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartTemplateCreation = () => {
    setIsCreatingFromTemplate(true);
    setShowEmptyBoardState(false);
    setShowSavedTemplates(true);
    setShowArchivedList(false);
    setTemplateActive(false);
  };

  const handleApplyTemplateToEmptyBoard = async (templateId) => {
    const name = projectDetail?.name?.trim();
    if (!name) {
      const message = 'Template name is required to create the board.';
      boardToast(message, TOAST_TYPE.ERROR);
      throw new Error(message);
    }

    const response = await APIServices.put(`/project-board/${selectedBoardId}`, {
      name,
      templateId
    });

    if (response?.status >= 200 && response?.status < 300) {
      boardToast(response.message || 'Board created from template successfully.', TOAST_TYPE.SUCCESS);
      setIsCreatingFromTemplate(false);
      setShowSavedTemplates(false);
      setTemplateActive(false);
      setActiveTemplateId('');
      await fetchProjectBoards(true, selectedBoardId);
      return;
    }

    const message = response?.data?.message || response?.data?.error || 'Failed to create board from template.';
    boardToast(message, TOAST_TYPE.ERROR);
    throw new Error(message);
  };

  const handleTemplateSelectionBack = async () => {
    if (isTemplateActive) {
      setTemplateActive(false);
      setActiveTemplateId('');
      setShowSavedTemplates(true);
      return;
    }

    setIsCreatingFromTemplate(false);
    setShowSavedTemplates(false);
    setTemplateActive(false);
    setActiveTemplateId('');
    setShowEmptyBoardState(true);
    await fetchProjectBoards(false);
  };

  const handlePreviewTemplateRenamed = (name) => {
    setProjectDetail((current) => (current ? { ...current, name } : current));
  };

  const handlePreviewTemplateDeleted = () => {
    const deletedTemplateId = activeTemplateId;
    setTemplates((current) => current.filter((template) => template._id !== deletedTemplateId));
    setTemplateActive(false);
    setActiveTemplateId('');
    setProjectDetail(undefined);
    setShowSavedTemplates(true);
  };

  useEffect(() => {
    if (socket) {
      const handle_Notification = () => setUnread(true);
      const handle_channels = (data) => {
        if (data.projectBoardId === projectId) {
          setChannels(data.data);
        }
      };
      socket?.on('Notification', handle_Notification);
      socket?.on('channels', handle_channels);
      return () => {
        socket?.off('Notification', handle_Notification);
        socket?.off('channels', handle_channels);
      };
    }
  }, [socket]);

  const showTabsBar =
    !isLoading &&
    !showEmptyBoardState &&
    !isCreatingFromTemplate &&
    !showSavedTemplates &&
    !showArchivedList &&
    selectedBoardId &&
    selectedBoardId !== 'NEW';
  const showOverviewView = boardViewType === 'overview' && isProjectRelatedBoard && projectId;

  useEffect(() => {
    if (showOverviewView && !isOverviewLoaded) {
      setIsOverviewLoaded(true);
    }
  }, [showOverviewView, isOverviewLoaded]);

  const handleTabChange = (tab) => {
    setBoardViewType(tab);

    localStorage.setItem('boardViewType', tab);
    // const params = new URLSearchParams(location.search);
    // params.set('tab', tab);

    // navigate(
    //   {
    //     pathname: location.pathname,
    //     search: params.toString()
    //   },
    //   { replace: true }
    // );
  };

  const userCanEditBoard = (board) => {
    if (!board || !user?._id) return false;

    const createdById = board.createdBy?._id || board.createdBy;
    if (createdById?.toString() === user._id.toString()) return true;

    const membership = board.teams?.find((team) => {
      const teamUserId = team.userId?._id || team.userId;
      return teamUserId?.toString() === user._id.toString();
    });

    return (membership?.permission?.name || membership?.permission) === 'Edit';
  };

  const selectedParentBoard =
    boards.find((board) => board._id === selectedParentBoardId) ||
    boards.find((board) => board._id === selectedBoardId);

  const HeaderContent = () => (
    <SocketProvider projectBoardId={selectedBoardId}>
      <ProjectBoardHeader
        projectOptions={projects}
        toggleModal={toggleModal}
        showBack={showBack}
        isUnread={isUnread}
        isTemplateActive={isTemplateActive}
        showSavedTemplates={showSavedTemplates}
        projectDetail={projectDetail}
        hideEditButton={false}
        projectBoardId={selectedBoardId}
        boards={boards}
        title={boardTitle}
        setBoards={setBoards}
        workSpaceTitle={workSpaceTitle}
        setBoardTitle={setBoardTitle}
        isFromHome={isFromHome}
        projectName={projectName}
        isProjectRelatedBoard={isProjectRelatedBoard}
        // toolbarOnly={showTabsBar}
      />
    </SocketProvider>
  );

  const renderContent = () => {
    if (showSavedTemplates || showArchivedList) {
      return (
        <SavedTemplates
          fetchTemplates={fetchTemplates}
          tempates={tempates}
          handleTemplateClick={handleTemplateClick}
          showArchivedList={showArchivedList}
          archivedTempates={archivedTempates}
          handleProjectClick={handleProjectClick}
        />
      );
    }
    if (selectedBoardId === 'NEW') {
      return (
        <div className="d-flex justify-content-between align-items-center">
          <EditableText
            title={''}
            handleSubmitTitle={handleSubmit}
            hideEditButton={false}
            defaultEditMode={true}
            handleChangeTitle={handleChangeTitle}
            handleCloseTitle={handleCloseTitle}
          />
          <div></div>
        </div>
      );
    }
    return (
      <ProjectBoardWrapperPage
        projectBoardId={selectedBoardId}
        setBoards={setBoards}
        channels={channels}
        projectDetail={projectDetail}
        isTemplateActive={isTemplateActive}
        fetchTemplates={fetchTemplates}
        activeTemplateId={activeTemplateId}
        archiveBoardId={archiveBoardId}
        parentBoardId={selectedParentBoardId}
        fetchProjectBoards={fetchProjectBoards}
        isArchivedActive={isArchivedActive}
        setArchiveBoardId={setArchiveBoardId}
        setArchivedActive={setArchivedActive}
        fetchArchivedBoards={fetchArchivedBoards}
        setBoardTitle={setBoardTitle}
        isProjectRelatedBoard={isProjectRelatedBoard}
        toggleModal={toggleModal}
        toggleBoardList={toggleBoardList}
        isArchivedParent={isArchivedParent}
        isBoardListCollapsed={isBoardListCollapsed}
        tabsAtTopLevel={true}
        boardViewType={boardViewType}
        setBoardViewType={setBoardViewType}
        onTemplateRenamed={handlePreviewTemplateRenamed}
        onTemplateDeleted={handlePreviewTemplateDeleted}
        onUseTemplate={isCreatingFromTemplate ? handleApplyTemplateToEmptyBoard : null}
      />
    );
  };

  return (
    <div className={`wrapper ${boardViewType === 'overview' ? 'overview-wrapper' : 'kanban-wrapper'}`}>
      <Header isBoard={true} isFromHome={isFromHome} content={HeaderContent()} />
      {/* <div className="main-content project-board" style={{ height: 'unset' }}></div> */}
      {showTabsBar && (
        <div className="project-board-subheader">
          {/* <div className="project-board-subheader__title">
            <SocketProvider projectBoardId={selectedBoardId}>
              <ProjectBoardTitle
                projectOptions={projects}
                isTemplateActive={isTemplateActive}
                showSavedTemplates={showSavedTemplates}
                projectDetail={projectDetail}
                hideEditButton={false}
                projectBoardId={selectedBoardId}
                title={boardTitle}
                setBoards={setBoards}
                setBoardTitle={setBoardTitle}
              />
            </SocketProvider>
          </div> */}
          <ViewTabsBar
            boardViewType={boardViewType}
            setBoardViewType={handleTabChange}
            // setBoardViewType={setBoardViewType}
            isProjectRelatedBoard={isProjectRelatedBoard}
            isTemplateActive={isTemplateActive}
            isArchivedParent={isArchivedParent}
            variant="subheader"
          />
        </div>
      )}
      <section
        className={`dashboard-sec ${isCreatingFromTemplate ? 'template-selection-mode' : ''} ${
          isCreatingFromTemplate && isTemplateActive ? 'template-selection-preview' : ''
        }`}>
        {isCreatingFromTemplate && !showEmptyBoardState && (
          <button type="button" className="template-selection-back" onClick={handleTemplateSelectionBack}>
            <img src={leftArrow} alt="" aria-hidden="true" />
            {/* <span>{isTemplateActive ? 'Back to templates' : ''}</span> */}
          </button>
        )}
        {isLoading ? (
          <div className="nodata-loader">{selectLoader(70)}</div>
        ) : showEmptyBoardState ? (
          <SocketProvider projectBoardId={selectedBoardId}>
            <EmptyBoardState
              onCreateBoard={handleAddBoard}
              boardId={selectedBoardId}
              socket={socket}
              setBoards={setBoards}
              setShowEmptyBoardState={setShowEmptyBoardState}
              onCreateWithTemplate={handleStartTemplateCreation}
              canEditBoard={userCanEditBoard(selectedParentBoard)}
            />
          </SocketProvider>
        ) : (
          <div className="container-fluid">
            <div className="row">
              {!showOverviewView &&
                boardViewType !== 'calendar' &&
                !(isCreatingFromTemplate && (showSavedTemplates || isTemplateActive)) && (
                <div className={`boardlist-col ${isBoardListCollapsed ? 'collapsed' : 'expanded'}`}>
                  <SocketProvider projectBoardId={selectedBoardId}>
                    <BoardList
                      boards={boards}
                      projectBoardId={selectedBoardId}
                      handleProjectClick={handleProjectClick}
                      handleAddBoard={handleAddBoard}
                      handleSubmit={handleSubmit}
                      showSavedTemplates={showSavedTemplates}
                      isTemplateActive={isTemplateActive}
                      setShowSavedTemplates={setShowSavedTemplates}
                      handleSavedTemplateClick={handleSavedTemplateClick}
                      showArchivedList={showArchivedList}
                      isArchivedActive={isArchivedActive}
                      isArchivedParent={isArchivedParent}
                      isCollapsed={isBoardListCollapsed}
                      handleEditTitle={handleSubmitTitle}
                      setBoards={setBoards}
                      currentUserId={user?._id}
                      canCreateBoard={userCanEditBoard(selectedParentBoard)}
                    />
                  </SocketProvider>
                </div>
              )}
              <div
                className={`board-content-col ${
                  isBoardListCollapsed ||
                  showOverviewView ||
                  boardViewType === 'calendar' ||
                  (isCreatingFromTemplate && (showSavedTemplates || isTemplateActive))
                    ? 'expanded'
                    : ''
                }`}>
                {showSavedTemplates || showArchivedList || selectedBoardId === 'NEW' ? (
                  renderContent()
                ) : (
                  <>
                    <div style={{ display: showOverviewView ? 'none' : 'block' }}>{renderContent()}</div>
                    {(isOverviewLoaded || showOverviewView) && (
                      <div style={{ display: showOverviewView ? 'block' : 'none' }}>
                        <UpdatedOverview embedded={true} setProjectName={setProjectName} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
      <SidebarModal show={modals.profileModal} onHide={() => toggleModal('profileModal', false)}>
        <ProfileModalContent
          profileUrl={profileUrl}
          formValues={formValues}
          isVerified={isVerified}
          toggleModal={toggleModal}
          userRole={userRole}
          hasPermission={hasPermission}
          logout={logout}
          subInfo={subInfo}
          templateBackUrl={`/project-board/${isProjectRelatedBoard ? 'project' : 'board'}/${selectedBoardId}`}
        />
      </SidebarModal>
      <SidebarModal
        extraClass={'edit-profile-modal'}
        show={modals.updateProfile}
        onHide={() => toggleModal('updateProfile', false)}
        headerTitle={ProjectListStrings.editProfileHeader}>
        <UpdateProfileModal
          show={modals.updateProfile}
          profileUrl={profileUrl}
          formValues={formValues}
          setError={setError}
          isVerified={isVerified}
          toggleModal={toggleModal}
          setFormValues={setFormValues}
          selectLoader={selectLoader}
          setProfileUrl={setProfileUrl}
        />
      </SidebarModal>
      <SidebarModal
        extraClass={'edit-profile-modal notification-modal'}
        show={modals.notifications}
        onHide={() => toggleModal('notifications', false)}
        headerTitle={'Happy Ending Project'}>
        <Notifications closeModal={() => toggleModal('notifications', false)} />
      </SidebarModal>
    </div>
  );
};

export default ProjectBoard;
